/**
 * Lead resolve on a signature request — V98-3 review R3-S3 / R3-S4 (server)
 * and the UI Blocker that goes with them: the band's "Resolve" on a
 * projected miss lands here, and a lead (owner/admin or the item's program
 * lead) sees what they can do about it, each action with its state:
 *
 *   Reassign the signer   only while the holder is away now (confirmed),
 *                         no longer assignable, or nobody is routed — a
 *                         member picker + a reason (+ optional end date);
 *                         otherwise the reason it isn't open yet.
 *   Ask to sign remotely  non-delegable kinds: a delivered ask to the away
 *                         approver of record (one open ask at a time).
 *   Extend the due date   soft dates only, never past the hard date.
 *
 * Reads: GET /coverage/items/signature_request/:id/lead-actions (403 for a
 * non-lead — the block then renders nothing). Writes: POST …/reassign-signer,
 * POST …/extend, POST /coverage/asks (kind sign_remote). The server enforces
 * every rule; this block only shows them.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Select, Textarea } from '@aigovops/design-system';
import { askToSignRemote, extendSignatureDue, getLeadActions, reassignSigner } from '../../lib/coverage-api';
import { firstName, fmtDay, nameOf, type Names } from '../../lib/today-model';
import { Chip, errorText } from '../today/shared';

type Done = (msg: string, focusId: string) => void;
type Open = null | 'reassign' | 'extend';

export function LeadActions({ slug, requestId, names, todayDay, onDone, fallback = null }: {
  slug: string; requestId: string; names: Names; todayDay: string; onDone: Done;
  /** What to show when the caller isn't a lead on this item after all (the server's 403) — e.g. the plain status. */
  fallback?: ReactNode;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['lead-actions', slug, requestId], queryFn: () => getLeadActions(slug, requestId), retry: false, staleTime: 30_000 });
  const [open, setOpen] = useState<Open>(null);
  const [asked, setAsked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (q.isLoading) return null;
  if (q.error || !q.data || !q.data.lead_actions.is_lead) return <>{fallback}</>;
  const la = q.data.lead_actions;
  const a = la.actions;
  const holder = la.holder.user_id ? nameOf(names, la.holder.user_id) : null;
  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['lead-actions', slug, requestId] }), qc.invalidateQueries({ queryKey: ['signature', slug, requestId] }),
      qc.invalidateQueries({ queryKey: ['today', slug] }), qc.invalidateQueries({ queryKey: ['blockers', slug] }),
    ]);
  };
  const ask = async () => {
    setError(null);
    try {
      await askToSignRemote(slug, { item_id: requestId, message: `Can you sign "${la.title}" remotely before ${fmtDay(la.hard_date ?? la.due_at)}?` });
      setAsked(`Asked ${firstName(holder ?? 'the approver')}`);
      await refresh();
      onDone(`Asked ${firstName(holder ?? 'the approver')} to sign remotely`, 'lead-sign-remote');
    } catch (e) { setError(errorText(e, 'The ask could not be sent.')); }
  };
  const miss = la.will_miss;
  return (
    <section className="gp-hat-block" aria-labelledby="lead-h" id="lead-actions" tabIndex={-1}>
      <h2 id="lead-h">As program lead</h2>
      <div className={`gp-state${miss ? ' gp-state--warn' : ''}`} id="lead-state" tabIndex={-1}>
        <strong>
          {holder ? `With ${holder}` : 'Nobody is routed to sign it'}
          {la.holder.away ? ` · away until ${fmtDay(la.holder.away.ends_on)}` : ''}
          {!la.holder.assignable && la.holder.user_id ? ' · no longer assignable' : ''}
        </strong>
        <span className="gp-ow-meta">
          {miss ? <Chip text={`Will miss ${fmtDay(miss.date)}`} tone="red" /> : null}
          {la.hard_date ? <span>{`Hard date ${fmtDay(la.hard_date)}`}</span> : null}
          {la.due_at ? <span>{`Due ${fmtDay(la.due_at)}`}</span> : null}
        </span>
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <ul className="gp-alts" aria-label="What you can do">
        <li id="lead-reassign" tabIndex={-1} data-available={a.reassign_signer.available || undefined}>
          <div className="gp-ow-line"><strong>Reassign the signer</strong>{a.reassign_signer.available ? null : <Chip text="Not yet" tone="lock" />}</div>
          <div className="gp-dim">{a.reassign_signer.why}</div>
          {a.reassign_signer.available ? (
            open === 'reassign'
              ? <ReassignForm slug={slug} requestId={requestId} fromUserId={a.reassign_signer.from_user_id} until={a.reassign_signer.until} names={names}
                  onCancel={() => { setOpen(null); window.setTimeout(() => document.getElementById('lead-reassign-open')?.focus(), 0); }}
                  onDone={async (m) => { setOpen(null); await refresh(); onDone(m, 'lead-state'); }} />
              : <div className="gp-actions"><Button compact id="lead-reassign-open" aria-expanded={false} onClick={() => setOpen('reassign')}>Reassign…</Button></div>
          ) : null}
        </li>
        {la.non_delegable ? (
          <li id="lead-sign-remote" tabIndex={-1}>
            <div className="gp-ow-line"><strong>{`Ask ${firstName(holder ?? 'the approver')} to sign remotely`}</strong>{a.sign_remote.open_ask_id || asked ? <Chip text={asked ?? 'Asked · waiting'} tone="lock" /> : null}</div>
            <div className="gp-dim">{a.sign_remote.why}</div>
            {a.sign_remote.available && !a.sign_remote.open_ask_id && !asked
              ? <div className="gp-actions"><Button compact onClick={() => void ask()}>Ask to sign remotely</Button></div> : null}
          </li>
        ) : null}
        <li id="lead-extend" tabIndex={-1}>
          <div className="gp-ow-line"><strong>Extend the due date</strong>{a.extend.available ? null : <Chip text={a.extend.hard ? 'Hard date' : 'Not available'} tone="lock" />}</div>
          <div className="gp-dim">{a.extend.why}</div>
          {a.extend.available ? (
            open === 'extend'
              ? <ExtendForm slug={slug} requestId={requestId} todayDay={todayDay} max={a.extend.max_due_at}
                  onCancel={() => { setOpen(null); window.setTimeout(() => document.getElementById('lead-extend-open')?.focus(), 0); }}
                  onDone={async (m) => { setOpen(null); await refresh(); onDone(m, 'lead-state'); }} />
              : <div className="gp-actions"><Button compact id="lead-extend-open" aria-expanded={false} onClick={() => setOpen('extend')}>Extend…</Button></div>
          ) : null}
        </li>
      </ul>
    </section>
  );
}

function ReassignForm({ slug, requestId, fromUserId, until, names, onCancel, onDone }: {
  slug: string; requestId: string; fromUserId: string | null; until: string | null; names: Names; onCancel: () => void; onDone: (m: string) => void;
}) {
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ to?: string; reason?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLSelectElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const people = [...names.keys()].filter((u) => u !== fromUserId);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (!to) errs.to = 'Choose who signs it.';
    if (reason.trim().length < 3) errs.reason = 'Say why (required).';
    setErrors(errs);
    if (errs.to || errs.reason) return;
    setBusy(true); setError(null);
    try {
      await reassignSigner(slug, requestId, { to_user_id: to, reason: reason.trim(), until });
      onDone(`Reassigned to ${nameOf(names, to)}${until ? ` until ${fmtDay(until)}` : ''}`);
    } catch (err) { setError(errorText(err, 'The signer could not be reassigned.')); }
    finally { setBusy(false); }
  };
  return (
    <form className="gp-form" onSubmit={submit} noValidate aria-label="Reassign the signer">
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="lead-reassign-to" label="Signs it instead" required hint="Someone outside the item's conflicting pairs" error={errors.to}>
        <Select ref={first} value={to} onChange={(e) => { setTo(e.currentTarget.value); setError(null); setErrors((x) => ({ ...x, to: undefined })); }}>
          <option value="">Choose…</option>
          {people.map((u) => <option key={u} value={u}>{nameOf(names, u)}</option>)}
        </Select>
      </Field>
      <Field id="lead-reassign-reason" label="Reason" required hint={until ? `Goes back to ${firstName(nameOf(names, fromUserId))} after ${fmtDay(until)}` : 'Kept in the item’s history'} error={errors.reason}>
        <Textarea rows={2} maxLength={2000} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setError(null); setErrors((x) => ({ ...x, reason: undefined })); }} />
      </Field>
      <div className="gp-actions">
        <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Reassign'}</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

function ExtendForm({ slug, requestId, todayDay, max, onCancel, onDone }: {
  slug: string; requestId: string; todayDay: string; max: string | null; onCancel: () => void; onDone: (m: string) => void;
}) {
  const [due, setDue] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ due?: string; reason?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const maxDay = max ? max.slice(0, 10) : undefined;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if (!due) errs.due = 'Choose the new due date.';
    else if (due < todayDay) errs.due = 'Today or later.';
    else if (maxDay && due > maxDay) errs.due = `On or before the hard date, ${fmtDay(maxDay)}.`;
    if (reason.trim().length < 3) errs.reason = 'Say why (required).';
    setErrors(errs);
    if (errs.due || errs.reason) return;
    setBusy(true); setError(null);
    try { await extendSignatureDue(slug, requestId, { due_at: `${due}T23:59:59.000Z`, reason: reason.trim() }); onDone(`Due date moved to ${fmtDay(due)}`); }
    catch (err) { setError(errorText(err, 'The due date could not be moved.')); }
    finally { setBusy(false); }
  };
  return (
    <form className="gp-form" onSubmit={submit} noValidate aria-label="Extend the due date">
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="lead-extend-due" label="New due date" required hint={maxDay ? `Up to the hard date, ${fmtDay(maxDay)}` : undefined} error={errors.due}>
        <Input ref={first} type="date" min={todayDay} max={maxDay} value={due} onChange={(e) => { setDue(e.currentTarget.value); setError(null); setErrors((x) => ({ ...x, due: undefined })); }} />
      </Field>
      <Field id="lead-extend-reason" label="Reason" required error={errors.reason}>
        <Textarea rows={2} maxLength={2000} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setError(null); setErrors((x) => ({ ...x, reason: undefined })); }} />
      </Field>
      <div className="gp-actions">
        <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Extend'}</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
